import { AnswerSurveyQuestionInput, OfficeApiService, SurveyAnswerValueType } from '@api_patient/office_api.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { ButtonArrange, CellulaFormSubmitData, Flex, Form, InputRating, InputTextArea, Text, toast } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { AnimateChangeInHeight } from '@component/animate_change_in_height.component';
import { Async, AsyncSignalValue } from '@component/async.component';
import { Shimmer } from '@component/new_shimmer.component';
import { TopBar } from '@component/top_bar.component';
import { UserSettingsService } from '@feature/settings/user_settings.service';
import { numberSafeParse } from '@module/safe_number_parse';
import { signal } from '@preact/signals-react';
import { ControllerProtocol, Injectable, useResolve, useResolveController } from '@visiba/instantiation';
import { AppointmentController } from '../view_appointment.controller';
import { BackgroundOutlet } from './_background_outlet';

export function ScreenEvaluationHcp() {
	const controller = useResolveController(EvaluationHcpController);

	const appointmentController = useResolve(AppointmentController);

	function handleGoBackIntercept(event: Event) {
		event.preventDefault();
		appointmentController.handleReconnect();
	}

	return (
		<Form.Root
			onSubmit={(d) => controller.handleSubmit(d)}
			options={{
				defaultValues: {},
			}}
		>
			<BackgroundOutlet>
				<TopBar.Outlet />
				<TopBar.Content goBack goBackIntercept={handleGoBackIntercept} />

				<Flex
					grow='1'
					style={{
						margin: `0 auto`,
						width: '100%',
						maxWidth: '600px',
					}}
					gap={tokens.size.x5}
				>
					<Flex gap={tokens.size.x1}>
						<Text.h1 variant='headingMedium'>{withTranslation('View.Appointment.ScreenEvaluationHcp.Title')}</Text.h1>
						<Text.p variant='bodyRegular'>{withTranslation('View.Appointment.ScreenEvaluationHcp.Description')}</Text.p>
					</Flex>

					<AnimateChangeInHeight>
						<Async derive={controller.state} fallback={<DynamicFormRootSkeleton />} fallbackDelay={0}>
							{(questions) => (
								<Flex gap={tokens.size.x4}>
									{questions.map((x) => {
										switch (x.type) {
											case 'Int4SatisfactionScore':
												return <QuestionTypeInt4SatisfactionScore key={x.alias} question={x} />;
											case 'Text500':
												return <QuestionTypeText500 key={x.alias} question={x} />;
											default:
												return null;
										}
									})}
								</Flex>
							)}
						</Async>
					</AnimateChangeInHeight>

					<Flex grow={1} justify='flex-end' gap={tokens.size.x3}>
						<Form.Error />
						<ButtonArrange>
							<Form.SubmitButton>{withTranslation('View.Appointment.ScreenEvaluationHcp.Continue')}</Form.SubmitButton>
						</ButtonArrange>
					</Flex>
				</Flex>
			</BackgroundOutlet>
		</Form.Root>
	);
}

interface QuestionTypeInt4SatisfactionScoreProps {
	question: Question;
}

function QuestionTypeInt4SatisfactionScore(props: QuestionTypeInt4SatisfactionScoreProps) {
	return (
		<Flex
			align='center'
			style={{
				margin: `${tokens.size.x5} 0`,
			}}
		>
			<InputRating.Root direction='horizontal' label={props.question.text} name={props.question.alias} max={4} />
		</Flex>
	);
}

interface QuestionTypeText500Props {
	question: Question;
}

function QuestionTypeText500(props: QuestionTypeText500Props) {
	return (
		<InputTextArea
			label={props.question.text}
			name={props.question.alias}
			minHeight={200}
			alwaysShowCharacterCount
			characterCount={500}
			options={{
				maxLength: 500,
			}}
		/>
	);
}

@Injectable()
class EvaluationHcpController implements ControllerProtocol {
	public state = signal<AsyncSignalValue<Question[]>>({
		value: null,
		flowState: 'pending',
	});
	private surveyAlias: Nullable<string> = null;

	constructor(
		private readonly universalConfig: UniversalConfig,
		private readonly appointmentController: AppointmentController,
		private readonly officeApiService: OfficeApiService,
		private readonly userSettingsService: UserSettingsService,
	) {
		// Empty
	}

	async whenMount() {
		this.surveyAlias = (this.appointmentController.appointmentInfo?.peek() ?? {}).isHost ? 'CallCompletedResource' : 'CallCompleted';
		const result = await this.officeApiService.survey(
			this.universalConfig.licenceHolderId,
			this.surveyAlias,
			this.userSettingsService.getSettings().selectedLanguage,
		);
		if (result.err) {
			this.state.value = {
				flowState: 'error',
				value: null,
			};

			return;
		}

		this.state.value = {
			flowState: 'accessible',
			value: result.ok.data.map((x) => ({
				id: x.SurveyQuestionId,
				alias: x.QuestionAlias,
				text: x.QuestionText ?? '',
				sortValue: x.SortValue,
				type: x.AnswerType,
			})),
		};
	}

	public async handleSubmit(data: CellulaFormSubmitData) {
		const payload = {
			BookingCallTicket: this.appointmentController.savedTicket ?? '',
			Answers: Object.entries(data)
				.filter(([, value]) => value != null && value !== '')
				.map(([key, value]) => this.createSurveyQuestionAnswer(key, value)),
		};

		const result = await this.officeApiService.surveyAnswers(
			this.universalConfig.licenceHolderId,
			this.surveyAlias ?? 'MISSING_ALIAS',
			payload,
		);
		if (result.err) {
			toast({
				label: I18nAndL10n.translate('PW.Common.Error.Unknown'),
				variant: 'danger',
			});

			return;
		}

		this.appointmentController.handleSurveyEnd();
	}

	private createSurveyQuestionAnswer(alias: string, value: string): AnswerSurveyQuestionInput {
		const parsed = numberSafeParse(value);
		if (parsed != null) {
			return {
				QuestionAlias: alias,
				IntValue: parsed,
				StringValue: null,
			};
		}

		return {
			QuestionAlias: alias,
			IntValue: null,
			StringValue: value,
		};
	}
}

interface Question {
	id: number;
	alias: string;
	text: string;
	sortValue: number;
	type: SurveyAnswerValueType;
}

function DynamicFormRootSkeleton() {
	return (
		<Flex
			gap={tokens.spacing.x3}
			style={{
				height: '400px',
			}}
		>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x3} width='60%' />
			</Flex>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x2} width='30%' />
				<Shimmer height={tokens.size.x9} />
			</Flex>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x2} width='42%' />
				<Shimmer height={tokens.size.x5} />
			</Flex>
		</Flex>
	);
}
